import React, {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, InputNumber, Spin } from 'antd';
import {
  ASSET_CLASS_PERCENT,
  CREATING,
  HISTORICAL_SCENARIO,
  PER_ASSET_NOTIONAL,
  RISK_WEIGHTED_APPROACH,
  TRAINER,
  selectors,
  updateNotionalLimitSettings,
  updateSessionUnderlyingAssets,
} from 'state';
import { appManager } from '../../state/app/manager';
import { InstrumentSelectionContext } from '../../views/TrainerSession/SessionSettings/instrumentSelectionContext';
import { useSessionCatalogue } from './SessionCatalogue';
import CatalogueBrowser from './CatalogueBrowser';
import { addSelected, removeSelected } from './filters';
import { enableFamilyRollover } from './api';
// Native eligibility scopes this picker without filtering the shared catalogue or legacy state.
import useFastAssetEligibility from '../../views/NativeSession/useFastAssetEligibility';
import FastAssetSelectionNotice from '../../views/NativeSession/FastAssetSelectionNotice';
import { useLegacyInstrumentVisibility } from '../instrumentVisibility/legacy';

const OldInstruments = lazy(
  () =>
    import(
      '../../views/TrainerSession/SessionSettingsDense/instruments_v2/InstrumentsPanelV2'
    ),
);

function LegacyInstruments({ readOnly }: { readOnly?: boolean }) {
  // The unchanged settings shells already own the shared legacy asset fetch.
  return (
    <Suspense fallback={<Spin />}>
      <OldInstruments readOnly={readOnly} />
    </Suspense>
  );
}

type Props = {
  readOnly?: boolean;
  // Opted in by the live-session shell; blueprint authoring stays unchanged.
  includeSavedSelection?: boolean;
};

export function NewSessionInstruments({
  readOnly,
  includeSavedSelection = false,
}: Props) {
  const dispatch = useDispatch();
  const userType = useSelector(selectors.userType);
  const session = useSelector(selectors.activeSession);
  const fastEligibility = useFastAssetEligibility(session);
  const currencies = useSelector(selectors.currencies);
  const context = useContext(InstrumentSelectionContext);
  const startedSessionId =
    includeSavedSelection && session.status !== CREATING
      ? session.id
      : undefined;
  const catalogue = useSessionCatalogue(
    startedSessionId,
    session.underlying_assets,
  );
  const [localSelected, setLocalSelected] = useState<number[]>(
    session.underlying_assets || [],
  );
  const [localLimits, setLocalLimits] = useState<Record<number, string>>({});
  const visibility = useLegacyInstrumentVisibility(session, readOnly);
  const [error, setError] = useState('');
  useEffect(() => {
    setLocalSelected(session.underlying_assets || []);
    setLocalLimits(
      Object.fromEntries(
        (session.underlying_asset_notional_limits || []).map(row => [
          row.underlying_asset,
          row.notional_limit,
        ]),
      ),
    );
  }, [
    session.id,
    session.underlying_assets,
    session.underlying_asset_notional_limits,
  ]);
  const selected =
    startedSessionId !== undefined
      ? session.underlying_assets || []
      : (context?.selected ?? localSelected);
  const setSelected = context?.setSelected ?? setLocalSelected;
  const limits = context?.notionalLimits ?? localLimits;
  const setLimits = context?.setNotionalLimits ?? setLocalLimits;
  const mode =
    context?.limitMode ?? session.instrument_limit_mode ?? ASSET_CLASS_PERCENT;
  const disabled = Boolean(
    readOnly ||
      session.status !== CREATING ||
      (context?.autoSaveDraft && !context.draftReady) ||
      // Wait for this definition's eligibility; stale or failed reads cannot expose unsupported rows.
      !fastEligibility.ready,
  );
  // Filter before family grouping, search and facet counts so every picker path uses the same IDs.
  const selectableCatalogue = useMemo(
    () =>
      catalogue.data && {
        ...catalogue.data,
        assets: catalogue.data.assets.filter(asset =>
          fastEligibility.allows(asset.id),
        ),
      },
    [catalogue.data, fastEligibility.allows],
  );
  const reloadCatalogue = () => {
    catalogue.reload();
    fastEligibility.reload();
  };
  const eligibility = useMemo(
    () =>
      new Map(
        (session.notional_limit_eligibility || []).map(row => [
          row.underlying_asset,
          row.eligible,
        ]),
      ),
    [session.notional_limit_eligibility],
  );
  const visibilityMode =
    session.status !== CREATING && session.which_markets === 4;
  const save = () => {
    setError('');
    if (
      mode === ASSET_CLASS_PERCENT &&
      (session.instrument_limit_mode ?? ASSET_CLASS_PERCENT) ===
        ASSET_CLASS_PERCENT &&
      !(session.underlying_asset_notional_limits || []).length
    ) {
      dispatch(
        updateSessionUnderlyingAssets({
          session_id: session.id,
          underlying_assets: selected,
        }),
      );
      return;
    }
    if (
      mode === PER_ASSET_NOTIONAL &&
      session.risk_approach === RISK_WEIGHTED_APPROACH
    ) {
      setError('Per-asset notional limits require the cash risk approach.');
      return;
    }
    if (selected.some(id => eligibility.get(id) === undefined)) {
      setError('Instrument-limit eligibility is still loading. Please retry.');
      return;
    }
    const eligible = selected.filter(id => eligibility.get(id));
    if (
      eligible.some(id =>
        !limits[id]?.trim()
          ? mode === PER_ASSET_NOTIONAL
          : !/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(limits[id].trim()),
      )
    ) {
      setError(
        'Enter a non-negative notional with at most two decimals for every eligible instrument.',
      );
      return;
    }
    dispatch(
      updateNotionalLimitSettings({
        session_id: session.id,
        instrument_limit_mode: mode,
        underlying_assets: selected,
        notional_limits: eligible
          .filter(id => limits[id]?.trim())
          .map(id => ({
            underlying_asset: id,
            notional_limit: limits[id].trim(),
          })),
      }),
    );
  };
  return (
    <>
      <FastAssetSelectionNotice
        eligibility={fastEligibility}
        selected={selected}
        disabled={disabled}
        onRemove={ids => setSelected(current => removeSelected(current, ids))}
      />
      {(error || context?.draftError) && (
        <Alert
          type="error"
          title={error || context?.draftError}
          action={
            context?.autoSaveDraft && context.draftError ? (
              <Button
                size="small"
                disabled={disabled}
                onClick={context.flushDraft}
                loading={context.draftSaving}
              >
                Retry save
              </Button>
            ) : undefined
          }
        />
      )}
      {context?.autoSaveDraft &&
        context.draftReady &&
        context.draftLaunchReady === false && (
          <Alert
            type="info"
            title="Complete the instrument limits and required currencies before starting this session."
          />
        )}
      {catalogue.error && (
        <Alert
          type="error"
          title={catalogue.error}
          action={<Button onClick={catalogue.reload}>Retry</Button>}
        />
      )}
      {catalogue.loading && !catalogue.data && <Spin />}
      {selectableCatalogue && (
        <CatalogueBrowser
          data={selectableCatalogue}
          startedSessionId={startedSessionId}
          visibility={visibility}
          restrictToCurated={userType === TRAINER}
          showAssetClass={userType !== TRAINER}
          reload={reloadCatalogue}
          toolbarActions={
            <>
              {session.status === CREATING && !context?.autoSaveDraft && (
                <Button
                  size="small"
                  type="primary"
                  disabled={disabled}
                  onClick={save}
                >
                  Save instruments
                </Button>
              )}
            </>
          }
          selected={selected}
          disabled={disabled}
          familyContext={{
            sessionId: session.id,
            historical: session.scenario_type === HISTORICAL_SCENARIO,
            live: session.live_synthetic_bonds_enabled,
            enableRollover: async signal => {
              const result = await enableFamilyRollover(session.id, signal);
              // Reuse the existing flag-only reducer, without a shared loading
              // spinner, refetching the session or replacing the selection draft.
              dispatch(
                appManager.socketEvents.toggle_afs_bond_switchover(result),
              );
            },
            key: JSON.stringify([
              session.id,
              session.start_date,
              session.end_date,
              session.frequency,
              session.scenario_type,
              session.time_advancement_type,
              session.scenario_schedule_dates,
              // Enabling rollover does not change the prepared issue IDs. Keep
              // its own response/socket update from cancelling confirmation.
              session.status,
            ]),
          }}
          onAdd={ids => {
            // Family/bulk selections must also stay within the native eligibility response.
            if (!disabled && ids.every(fastEligibility.allows))
              setSelected(current => addSelected(current, ids));
          }}
          onRemove={ids => {
            if (!disabled) setSelected(current => removeSelected(current, ids));
          }}
          selectedExtra={id =>
            mode === PER_ASSET_NOTIONAL && !visibilityMode ? (
              eligibility.get(id) ? (
                <label className="ac-limit">
                  Notional limit (
                  {currencies[session.reporting_currency]?.symbol ||
                    'reporting CCY'}
                  )
                  <InputNumber
                    stringMode
                    min="0"
                    precision={2}
                    controls={false}
                    value={limits[id] || null}
                    disabled={disabled}
                    aria-label={`Notional limit for asset ${id}`}
                    onChange={value =>
                      setLimits(current => ({
                        ...current,
                        [id]: value === null ? '' : String(value),
                      }))
                    }
                    onBlur={() => context?.flushDraft?.()}
                  />
                </label>
              ) : (
                <small>Uses existing product limit</small>
              )
            ) : null
          }
        />
      )}
    </>
  );
}

export default function SessionInstrumentsSwitch({
  readOnly,
  includeSavedSelection,
}: Props) {
  // Same URL-only, read-on-mount pattern as useClassicCreateModal.
  // Load with ?oldSelect=1 to use the legacy selector; remove it and reload
  // to return to the new selector. Stored view preferences are not consulted.
  const [oldSelect] = useState(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get('oldSelect') === '1'
      );
    } catch {
      return false;
    }
  });
  return (
    <div className="ac-root ac-session">
      {oldSelect ? (
        <LegacyInstruments readOnly={readOnly} />
      ) : (
        <NewSessionInstruments
          readOnly={readOnly}
          includeSavedSelection={includeSavedSelection}
        />
      )}
    </div>
  );
}
